import LaunchOutlined from "@mui/icons-material/LaunchOutlined";
import { IconButton, Tooltip, ListSubheader, ListSubheaderProps } from "@mui/material";
import { gql } from "generated-graphql";
import {
  FireDepartmentPickerFragment,
  GroupedFireDepartmentSearchQuery,
  GroupedFireDepartmentSearchQueryVariables,
} from "generated-graphql/graphql";
import { useValidatingFormContext } from "hooks/useValidatingForm";
import { forwardRef } from "react";
import { ControllerFieldState } from "react-hook-form";

import { DisplayOnlyField } from "../../../../../../components/Forms/DisplayOnlyField";
import GroupedAsyncAutocomplete from "../../../../../../components/Forms/GroupedAsyncAutocomplete";

// Base props for the grouped fire department picker
type GroupedFireDepartmentPickerProps = {
  facilityIds: string[];
  onChange?: (value: GroupedFireDepartment | null) => void;
  value?: GroupedFireDepartment | null;
  disabled?: boolean;
  helperText?: string | React.JSX.Element;
  displayAgencyLink?: boolean;
} & ControllerFieldState;

// Extended FireDepartment type with grouping information
export type GroupedFireDepartment = FireDepartmentPickerFragment & {
  group: "primary" | "supplemental";
  isPrimary: boolean;
};

const GROUPED_FIRE_DEPARTMENTS_SEARCH = gql(`
  query GroupedFireDepartmentSearch($facilityIds: [String!]!, $search: String, $page: Int, $pageSize: Int, $sort: [SortModel!]) {
    groupedFireDepartmentSearch(facilityIds: $facilityIds, search: $search, page: $page, pageSize: $pageSize, sort: $sort) {
      primary {
        ...FireDepartmentPicker
      }
      supplemental {
        ...FireDepartmentPicker
      }
      primaryCount
      supplementalCount
    }
  }
`);

// Shared function to format Fire Department option labels
const getFireDepartmentOptionLabel = (fireDepartment: GroupedFireDepartment) => {
  return [fireDepartment.name, fireDepartment.city, fireDepartment.county, fireDepartment.state]
    .filter(Boolean)
    .join(", ");
};

// Custom ListSubheader for fire department groups
function FireDepartmentGroupHeader(props: ListSubheaderProps) {
  return (
    <ListSubheader
      {...props}
      sx={{
        fontWeight: "bold",
        backgroundColor: "background.paper",
        borderBottom: "1px solid",
        borderColor: "divider",
      }}
    />
  );
}
FireDepartmentGroupHeader.muiSkipListHighlight = true;

// Facility-based grouped picker with primary and supplemental fire departments
export const GroupedFireDepartmentPickerWithFacilityFilter = forwardRef<
  HTMLDivElement,
  GroupedFireDepartmentPickerProps
>(function GroupedFireDepartmentPickerWithFacilityFilter(
  { facilityIds, onChange, value, error, disabled, helperText, displayAgencyLink },
  ref
) {
  const formContext = useValidatingFormContext();

  // Transform query response into grouped format
  const getGroupedItems = (data: GroupedFireDepartmentSearchQuery): GroupedFireDepartment[] => {
    const primaryItems: GroupedFireDepartment[] = data.groupedFireDepartmentSearch.primary.map(
      (dept) => ({
        ...dept,
        group: "primary" as const,
        isPrimary: true,
      })
    );

    const supplementalItems: GroupedFireDepartment[] =
      data.groupedFireDepartmentSearch.supplemental.map((dept) => ({
        ...dept,
        group: "supplemental" as const,
        isPrimary: false,
      }));

    // Combine and deduplicate by ID
    // A fire department might be both primary and supplemental if it's linked to multiple facilities
    // This is unlikely but possible.
    const allItems = [...primaryItems, ...supplementalItems];
    const uniqueItems = allItems.reduce((acc, item) => {
      const existing = acc.find((existing) => existing.id === item.id);
      if (!existing) {
        acc.push(item);
      } else if (item.isPrimary && !existing.isPrimary) {
        // If we have both primary and supplemental for the same department, prefer primary
        const index = acc.indexOf(existing);
        acc[index] = item;
      }
      return acc;
    }, [] as GroupedFireDepartment[]);

    return uniqueItems;
  };

  // Group by function for the autocomplete
  const groupBy = (option: GroupedFireDepartment) => option.group;

  // Custom group renderer with friendly labels
  const renderGroup = (params: any) => (
    <li key={params.key}>
      <FireDepartmentGroupHeader>
        {params.group === "primary" ? "Primary Fire Departments" : "Supplemental Fire Departments"}
      </FireDepartmentGroupHeader>
      <ul style={{ padding: 0 }}>{params.children}</ul>
    </li>
  );

  // Compose facility filter for search
  const facilityFilter = facilityIds.length ? `facilityId:${facilityIds.join("|")}` : "";

  if (formContext?.metadata?.isDisplayOnly) {
    return (
      <DisplayOnlyField text={value ? getFireDepartmentOptionLabel(value) : ""} label={"FD"} />
    );
  }

  return (
    <GroupedAsyncAutocomplete<
      GroupedFireDepartmentSearchQuery,
      GroupedFireDepartment,
      "primary" | "supplemental",
      GroupedFireDepartmentSearchQueryVariables
    >
      query={GROUPED_FIRE_DEPARTMENTS_SEARCH}
      variables={{
        facilityIds,
      }}
      getItems={getGroupedItems}
      getOptionLabel={getFireDepartmentOptionLabel}
      groupBy={groupBy}
      groupOrder={["primary", "supplemental"]}
      renderGroup={renderGroup}
      onChange={onChange}
      value={value || null}
      error={error}
      disabled={disabled}
      additionalSearchString={facilityFilter}
      helperText={helperText}
      additionalEndAdornment={displayAgencyLink && agencyLinkAdornment(value)}
      placeholderLabel="Search Fire Departments"
    />
  );
});

function agencyLinkAdornment(value: GroupedFireDepartment | null | undefined) {
  return (
    value && (
      <Tooltip title="Open Fire Department">
        <IconButton
          size="small"
          sx={{
            padding: "4px",
            color: "primary.main",
          }}
          onClick={(e) => {
            e.stopPropagation(); // Prevent triggering the dropdown
            window.open(`/staff/agencies/fireDepartments?omnisearch=id:${value.id}`, "_blank");
          }}
        >
          <LaunchOutlined fontSize="small" />
        </IconButton>
      </Tooltip>
    )
  );
}
